import { ChangeDetectionStrategy, Component, Input, OnDestroy, inject } from '@angular/core'
import { LoggerFactory } from '@elderbyte/ts-logger'
import {
  DataContextBuilder,
  ElderDataToolbarComponent,
  ElderSearchBoxComponent,
  ElderSearchContextDirective,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderToolbarContentDirective,
  ElderTruncatePipe,
  Filter,
  IDataContext,
} from '@elderbyte/ngx-starter'
import { Router } from '@angular/router'
import { Property, PropertyKind, PropertyType } from '../property'
import { PropertyFilter, PropertyService } from '../property.service'
import { coerceBooleanProperty } from '@angular/cdk/coercion'
import { ReplaySubject } from 'rxjs'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { AsyncPipe } from '@angular/common'
import { MatIcon } from '@angular/material/icon'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { MatSort, MatSortHeader } from '@angular/material/sort'

@Component({
  selector: 'app-property-browser',
  templateUrl: './property-browser.component.html',
  styleUrls: ['./property-browser.component.scss'],
  imports: [
    ElderTableComponent,
    MatSort,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    ElderToolbarContentDirective,
    ElderSearchBoxComponent,
    ElderSearchContextDirective,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIcon,
    MatSortHeader,
    AsyncPipe,
    ElderTranslatePipe,
    ElderTruncatePipe,
  ],
})
export class PropertyBrowserComponent implements OnDestroy {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)
  public readonly data: IDataContext<Property>
  public readonly forcedFilters$ = new ReplaySubject<Filter[]>(1)
  public readonly PropertyType = PropertyType
  private router = inject(Router)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const propertyService = inject(PropertyService)
    this.data = DataContextBuilder.start<Property>()
      .pageSize(50)
      .reloadOnLocalChanges()
      .autoStartOnFiltersAll([PropertyFilter.KINDS_KEY])
      .build(propertyService)
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnDestroy(): void {
    if (this.data) {
      this.data.close()
    }
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set kinds(kinds: PropertyKind[]) {
    if (kinds) {
      const filter = PropertyFilter.kinds(...kinds)
      this.log.debug('Updating property filter', filter)
      this.forcedFilters$.next([filter])
    }
  }

  @Input()
  public set productKind(value: any) {
    if (coerceBooleanProperty(value)) {
      this.kinds = [PropertyKind.PRODUCT, PropertyKind.ITEM]
    }
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public openDetail(property: Property): void {
    this.router.navigate([{ outlets: { side: ['properties', property.id] } }], {
      preserveFragment: true,
    })
  }

  public create(): void {
    this.router.navigate([{ outlets: { side: ['properties', 'new'] } }], { preserveFragment: true })
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  renderRoundingMode({ precision, roundingMode }: Property): string {
    const isConfigured = !!precision || precision === 0
    if (isConfigured) {
      return roundingMode
    } else {
      return '' // hide
    }
  }
}
